/**
 * OverlapAudienceProfile Component
 *
 * Displays the demographic profile of the shared audience using Discovery chart components.
 * Renders a grid of DonutCharts, VerticalBarCharts, HorizontalBarCharts, and GaugeCharts
 * for each dimension, reusing the same chart DNA from Discovery.
 */

import { useMemo } from 'react'
import type { ReactNode } from 'react'
import type { SubjectAggregations, DistributionItem } from '@/types/comparison'
import { DonutChart } from '../charts/components/DonutChart'
import { HorizontalBarChart } from '../charts/components/HorizontalBarChart'
import { VerticalBarChart } from '../charts/components/VerticalBarChart'
import { GaugeChart } from '../charts/components/GaugeChart'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface OverlapAudienceProfileProps {
  readonly aggregations: SubjectAggregations
  readonly overlapCount: number
  readonly className?: string
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type ChartItem = { readonly label: string; readonly value: number }

/**
 * Convert DistributionItem[] to chart data format, sorted desc and limited to top N.
 */
function toChartData(
  items: readonly DistributionItem[] | undefined,
  maxItems = 5,
): ReadonlyArray<ChartItem> {
  if (!items || !Array.isArray(items)) return []
  return [...items]
    .sort((a, b) => b.count - a.count)
    .slice(0, maxItems)
    .map((item) => ({ label: item.name, value: item.count }))
}

function formatCompact(value: number): string {
  const absValue = Math.abs(value)
  if (absValue >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`
  if (absValue >= 1_000) return `${(value / 1_000).toFixed(1)}K`
  return String(value)
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function OverlapAudienceProfile({
  aggregations,
  overlapCount,
  className = '',
}: OverlapAudienceProfileProps) {
  const total = aggregations.vanity.totalProfiles

  // All hooks must be called before any early return (Rules of Hooks)
  const genderData = useMemo(() => {
    const items = aggregations.demographics.gender
    if (!items || !Array.isArray(items)) return []
    return [...items]
      .filter((item) => item.name.toLowerCase() !== 'other')
      .sort((a, b) => b.count - a.count)
      .map((item) => ({ label: item.name.charAt(0).toUpperCase() + item.name.slice(1).toLowerCase(), value: item.count }))
  }, [aggregations.demographics.gender])
  const ageData = useMemo(
    () => toChartData(aggregations.demographics.age, 6),
    [aggregations.demographics.age],
  )
  const platformData = useMemo(
    () => toChartData(aggregations.platform),
    [aggregations.platform],
  )
  const countryData = useMemo(
    () => toChartData(aggregations.demographics.countries),
    [aggregations.demographics.countries],
  )
  const followerTierData = useMemo(
    () => toChartData(aggregations.followerTier),
    [aggregations.followerTier],
  )
  const emotionData = useMemo(
    () => toChartData(aggregations.emotions),
    [aggregations.emotions],
  )
  const ethnicityData = useMemo(
    () => toChartData(aggregations.demographics.ethnicities),
    [aggregations.demographics.ethnicities],
  )
  const languageData = useMemo(() => {
    const items = aggregations.languages
    if (!items || !Array.isArray(items)) return []
    return toChartData(items.filter((item) => item.name && item.name.toLowerCase() !== 'undefined'))
  }, [aggregations.languages])

  // Sentiment: build from comment stats (not a distribution array)
  const sentimentData = useMemo(() => {
    if (!aggregations.comments || aggregations.comments.total_comments === 0) return []
    return [
      { label: 'Positive', value: aggregations.comments.sentiment_positive },
      { label: 'Neutral', value: aggregations.comments.sentiment_neutral },
      { label: 'Negative', value: aggregations.comments.sentiment_negative },
    ]
  }, [aggregations.comments])

  // Avg sentiment for gauge (0-10 scale)
  const avgSentiment = useMemo(
    () => aggregations.comments?.avg_sentiment ?? aggregations.vanity.avgSentiment ?? 0,
    [aggregations.comments, aggregations.vanity.avgSentiment],
  )

  // Build chart cards config: [key, hasData, renderFn]
  const charts = useMemo(() => {
    const items: ReadonlyArray<{ key: string; hasData: boolean; render: () => ReactNode }> = [
      {
        key: 'gender',
        hasData: genderData.length > 0,
        render: () => (
          <DonutChart
            data={genderData}
            title="Gender"
            centerLabel="total"
            size={140}
            colorOffset={0}
          />
        ),
      },
      {
        key: 'platform',
        hasData: platformData.length > 0,
        render: () => (
          <VerticalBarChart
            data={platformData}
            title="Platform"
            showIcons
          />
        ),
      },
      {
        key: 'age',
        hasData: ageData.length > 0,
        render: () => (
          <HorizontalBarChart
            data={ageData}
            title="Age Groups"
            showRankMedals={false}
          />
        ),
      },
      {
        key: 'country',
        hasData: countryData.length > 0,
        render: () => (
          <HorizontalBarChart
            data={countryData}
            title="Top Countries"
            showRankMedals
          />
        ),
      },
      {
        key: 'emotion',
        hasData: emotionData.length > 0,
        render: () => (
          <DonutChart
            data={emotionData}
            title="Emotions"
            centerLabel="total"
            size={140}
            colorOffset={4}
          />
        ),
      },
      {
        key: 'followerTier',
        hasData: followerTierData.length > 0,
        render: () => (
          <HorizontalBarChart
            data={followerTierData}
            title="Follower Tier"
            showRankMedals={false}
          />
        ),
      },
      {
        key: 'ethnicity',
        hasData: ethnicityData.length > 0,
        render: () => (
          <DonutChart
            data={ethnicityData}
            title="Ethnicity"
            centerLabel="total"
            size={140}
            colorOffset={2}
          />
        ),
      },
      {
        key: 'language',
        hasData: languageData.length > 0,
        render: () => (
          <HorizontalBarChart
            data={languageData}
            title="Top Languages"
            showRankMedals
          />
        ),
      },
      {
        key: 'sentiment',
        hasData: sentimentData.length > 0,
        render: () => (
          <GaugeChart
            value={avgSentiment}
            maxValue={10}
            title="Comment Sentiment"
            label={`Avg: ${avgSentiment.toFixed(1)}`}
          />
        ),
      },
    ]

    return items.filter((item) => item.hasData)
  }, [
    genderData, platformData, ageData, countryData, emotionData,
    followerTierData, ethnicityData, languageData, sentimentData, avgSentiment,
  ])

  // Early returns after all hooks (Rules of Hooks compliance)
  if (total === 0) return null
  if (charts.length === 0) return null

  return (
    <div className={className}>
      {/* Section Header */}
      <div className="mb-4">
        <h3 className="text-base font-semibold text-v11-text-primary">
          Shared Audience Profile
        </h3>
        <p className="text-sm text-v11-text-muted">
          Demographics of the {formatCompact(overlapCount)} profiles that belong to both
          audiences
        </p>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {charts.map((chart) => (
          <div key={chart.key}>
            {chart.render()}
          </div>
        ))}
      </div>
    </div>
  )
}
